import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, TrendingUp, ArrowRight, ChevronUp, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { CURRENCY_OPTIONS } from '@/components/business/features/custom-adventures/components/adventure-builder/constants';

// Import proven business calculator logic
import { calculateAllOptionsPricing } from '@/components/business/features/custom-adventures/components/adventure-builder/hooks/optionPricingCalculator';
import { 
  calculateScalableBasePrice,
  getBasePriceDescription 
} from '@/components/business/features/custom-adventures/components/adventure-builder/hooks/basePricingCalculator';
import { detectPeopleValue, detectDaysValue, roundPrice } from '@/components/business/features/custom-adventures/components/adventure-builder/hooks/pricingUtils';
import { DAYS_KEYWORDS } from '@/components/business/features/custom-adventures/components/adventure-builder/hooks/pricing-types';

interface PublicPriceCalculatorProps {
  adventure: CustomAdventure;
  selectedOptions: Record<string, any>;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  className?: string;
  onPriceUpdate?: (totalPrice: number, currency: string, breakdown: PriceBreakdownItem[]) => void;
}

interface PriceBreakdownItem {
  id: string;
  label: string;
  description: string;
  amount: number;
  type: 'base' | 'option';
}

export function PublicPriceCalculator({ 
  adventure, 
  selectedOptions, 
  dateRange,
  className = "",
  onPriceUpdate
}: PublicPriceCalculatorProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastPrice, setLastPrice] = useState(adventure.basePrice);
  const [priceChanged, setPriceChanged] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  // Calculate days from date range
  const calculatedDays = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      const diffTime = Math.abs(dateRange.to.getTime() - dateRange.from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 1; // Default to 1 day if no date range
  }, [dateRange]);

  // Get currency symbol
  const currencySymbol = useMemo(() => {
    return CURRENCY_OPTIONS.find(c => c.value === adventure?.currency)?.symbol || '€';
  }, [adventure?.currency]);

  // Calculate pricing breakdown using proven business logic
  const calculation = useMemo(() => {
    // Early return if adventure is not loaded yet
    if (!adventure) {
      console.log('🔍 [PublicPriceCalculator] Adventure not loaded yet, returning default calculation');
      return {
        breakdown: [],
        totalPrice: 0,
        currencySymbol: '€',
        scaledBasePrice: 0,
        basePrice: 0
      };
    }

    console.log('🔍 [PublicPriceCalculator] Starting calculation with:', {
      adventure: adventure.name,
      basePrice: adventure.basePrice,
      basePriceType: adventure.basePriceType || 'fixed',
      currency: adventure.currency,
      selectedOptions,
      calculatedDays,
      dateRange
    });

    // Auto-detect people and days values using business logic
    const peopleValue = detectPeopleValue(adventure.options || [], selectedOptions);
    const daysValue = calculatedDays; // Use days from date picker instead of form

    console.log('🔍 [PublicPriceCalculator] Detected values:', {
      peopleValue,
      daysValue: daysValue,
      daysSource: 'dateRange'
    });

    // Calculate scalable base price using business logic
    const basePriceType = (adventure.basePriceType || 'fixed') as 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
    const scaledBasePrice = calculateScalableBasePrice(adventure.basePrice, basePriceType, peopleValue, daysValue);

    console.log('🔍 [PublicPriceCalculator] Base price calculation:', {
      originalBasePrice: adventure.basePrice,
      basePriceType,
      scaledBasePrice,
      scaling: scaledBasePrice !== adventure.basePrice ? `${peopleValue} people × ${daysValue} days` : 'none'
    });

    // Calculate all options pricing using proven business logic
    const { totalAmount: optionsAmount, breakdownItems } = calculateAllOptionsPricing(
      adventure.options || [],
      selectedOptions,
      scaledBasePrice,
      peopleValue,
      daysValue,
      {}, // Legacy multipliers (empty for public form)
      currencySymbol
    );

    console.log('🔍 [PublicPriceCalculator] Options pricing:', {
      optionsAmount,
      breakdownItemsCount: breakdownItems.length
    });

    // Build final breakdown with explicit math formulas
    const basePriceDescription = getBasePriceDescription(
      basePriceType,
      adventure.basePrice,
      peopleValue,
      daysValue,
      currencySymbol
    );
    
    const breakdown: PriceBreakdownItem[] = [
      {
        id: 'base',
        label: adventure.name,
        description: basePriceDescription,
        amount: scaledBasePrice,
        type: 'base'
      },
      // Convert business breakdown items to public format (descriptions already include formulas)
      ...breakdownItems.map(item => ({
        id: item.id,
        label: item.label,
        description: item.description || `${currencySymbol}${item.amount.toFixed(2)}`,
        amount: item.amount,
        type: 'option' as const
      }))
    ];

    const totalPrice = roundPrice(scaledBasePrice + optionsAmount);

    console.log('🔍 [PublicPriceCalculator] Final calculation result:', {
      basePrice: adventure.basePrice,
      scaledBasePrice,
      optionsAmount,
      totalPrice,
      breakdown
    });

    return {
      breakdown,
      totalPrice,
      basePrice: adventure.basePrice || 0, // Safe fallback for undefined basePrice
      scaledBasePrice,
      optionsCount: breakdownItems.length
    };
  }, [adventure, selectedOptions, calculatedDays, currencySymbol]);

  // Animate price changes
  useEffect(() => {
    if (calculation.totalPrice !== lastPrice) {
      setPriceChanged(true);
      setLastPrice(calculation.totalPrice);
      setAnimationKey(prev => prev + 1);
      
      const timer = setTimeout(() => {
        setPriceChanged(false);
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [calculation.totalPrice, lastPrice]);

  // Notify parent of price updates
  useEffect(() => {
    if (onPriceUpdate && adventure && calculation.totalPrice > 0) {
      onPriceUpdate(calculation.totalPrice, adventure.currency || 'EUR', calculation.breakdown);
    }
  }, [calculation.totalPrice, adventure?.currency, calculation.breakdown, onPriceUpdate]);

  const priceVariants = {
    normal: {
      scale: 1,
      color: '#ffffff'
    },
    changed: {
      scale: [1, 1.15, 1],
      color: ['#ffffff', '#f0f9ff', '#ffffff'],
      transition: {
        duration: 0.6,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xl w-full ${className}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-green-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            <h5 className="font-semibold">Live Price</h5>
            <Badge variant="secondary" className="text-xs bg-white/20 text-white border-white/30">
              Real-time
            </Badge>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 p-0 text-white hover:bg-white/20"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>

        {/* Total Price */}
        <div className="mt-3">
          <motion.div
            key={animationKey}
            variants={priceVariants}
            animate={priceChanged ? 'changed' : 'normal'}
            className="text-2xl font-bold"
          >
            €{calculation.totalPrice.toFixed(2)}
          </motion.div>
          <div className="text-sm text-white/80">
            {calculation.optionsCount} option{calculation.optionsCount !== 1 ? 's' : ''} selected
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ 
            height: isExpanded ? 'auto' : 0, 
            opacity: isExpanded ? 1 : 0 
          }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="p-4 space-y-4">
            {/* Price Breakdown */}
            <div className="space-y-4">
              {/* Base Price Section */}
              {calculation.breakdown.filter(item => item.type === 'base').length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Base Price</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                    {calculation.breakdown
                      .filter(item => item.type === 'base')
                      .map((item) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="pb-2 last:pb-0"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{item.label}</div>
                            </div>
                            <div className="text-right ml-3 flex-shrink-0">
                              <div className="font-bold text-primary text-lg">
                                {currencySymbol}{(item.amount || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                          {item.description && (
                            <div className="text-xs text-blue-600 font-mono mt-1">
                              {item.description}
                            </div>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Add-ons Section */}
              {calculation.breakdown.filter(item => item.type === 'option').length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Add-ons & Extras</h4>
                  <div className="space-y-2">
                    {calculation.breakdown
                      .filter(item => item.type === 'option')
                      .map((item) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="border-b border-gray-100 last:border-b-0 pb-2 last:pb-0"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{item.label}</div>
                            </div>
                            <div className="text-right ml-3 flex-shrink-0">
                              <div className="font-semibold text-gray-900">
                                {currencySymbol}{(item.amount || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                          {item.description && (
                            <div className="text-xs text-blue-600 font-mono mt-0.5">
                              {item.description}
                            </div>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Show when no options are selected */}
              {calculation.optionsCount === 0 && (
                <div className="text-center py-3 text-gray-500 text-sm italic">
                  Select options above to see pricing updates
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div className="border-t pt-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-center">
                  <div className="font-bold text-primary">
                    €{(calculation.basePrice || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-500 text-xs">Base Price</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-blue-600">
                    {calculation.optionsCount}
                  </div>
                  <div className="text-gray-500 text-xs">Add-ons</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <TrendingUp className="w-3 h-3" />
                  <div className="flex-1 bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-primary h-1 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, ((calculation.basePrice || 0) / calculation.totalPrice) * 100)}%` 
                      }}
                    />
                  </div>
                  <span>
                    {Math.round(((calculation.basePrice || 0) / calculation.totalPrice) * 100)}% base
                  </span>
                </div>
              </div>

              {/* Value-Added Information */}
              <div className="mt-3 space-y-2">
                {/* Pricing Note */}
                <div className="p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <div className="flex items-center gap-1">
                    <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                    Final pricing confirmed by business owner
                  </div>
                </div>
                
                {/* Quick Benefits */}
                <div className="p-2 bg-green-50 rounded text-xs">
                  <div className="font-medium text-green-800 mb-1">Included Benefits:</div>
                  <div className="space-y-1 text-green-700">
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                      Expert wildlife guide
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                      Equipment provided
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                      Small group experience
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                {calculation.breakdown.length > 1 && (
                  <div className="p-2 bg-purple-50 rounded text-xs">
                    <div className="font-medium text-purple-800 mb-1">Next Steps:</div>
                    <div className="text-purple-700">
                      Complete your details below to submit your booking request
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
